import type { ApolloClientIntegration } from '@apollo/client-integration-tanstack-start'
import { SideNavCookieProvider } from '@repo/shared-client/components/blocks'
import { TooltipProvider } from '@repo/shared-client/components/ui'
import { isAtLeastRole, isValidRole, UserRole } from '@repo/shared-common'
import { TanStackDevtools } from '@tanstack/react-devtools'
import type { QueryClient } from '@tanstack/react-query'
import { createRootRouteWithContext, HeadContent, Scripts } from '@tanstack/react-router'
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools'
import { PullToRefreshProvider } from '@web/src/contexts/pull-to-refresh-context'
import { PushNotificationProvider } from '@web/src/contexts/push-notification-context'
import { PwaProvider } from '@web/src/contexts/pwa-context'
import { SFProvider } from '@web/src/contexts/sf-context'
import { authQueryKeys } from '@web/src/hooks/use-auth'
import { DevToolLinks } from '@web/src/integrations/custom-devtools/devtool-links'
import type { auth } from '@web/src/lib/auth/auth'
import { authUserSession } from '@web/src/lib/auth/auth-server'
import i18n from '@web/src/lib/intl/i18n'
import {
  ensureServiceWorkerRegistration,
  isServiceWorkerEnabled,
  removeServiceWorkers,
} from '@web/src/lib/pwa/service-worker-registration'
import { canonicalUrlForPathname, seo } from '@web/src/lib/seo'
import { useEffect } from 'react'
import { Toaster } from 'react-hot-toast'
import { I18nextProvider } from 'react-i18next'
import { initGA, logPageView } from '../analytics'
import { ColorSchemeProvider } from '../contexts/color-scheme-context'
import CookieConsentBanner from '../features/cookie-consent/cookie-consent-banner'
import PwaInstallBar from '../features/pwa-install/pwa-install-bar'
import { SentryUserSync } from '../features/sentry-user-sync/sentry-user-sync'
import TanStackQueryDevtools from '../integrations/tanstack-query/devtools'
import { generateWindowConfigScript } from '../lib/env.dynamic.client'
import { readSideNavSsrCookies } from '../lib/side-nav/read-side-nav-ssr-cookies'
import type { BetterAuthSessionData } from '../lib/yoga/yoga-auth-context'
import appCss from '../styles.css?url'
import tailwindCss from '../tailwind.css?url'
import { holidayAttributeForTheme, isThemeName, resolveActiveTheme } from '../theme'
import { useDisableSeasonalTheme } from '../theme/use-disable-seasonal-theme'
import { getMapboxToken } from '../utils/mapbox-token'

// Infer the Session type from better-auth
type Session = typeof auth.$Infer.Session

interface CustomRouterContext extends ApolloClientIntegration.RouterContext {
  queryClient: QueryClient
  session?: Session
}

export const Route = createRootRouteWithContext<CustomRouterContext>()({
  head: ({ matches }) => {
    // Get the last match in the matches array which is the most specific match
    const lastMatch = matches[matches.length - 1]
    // Only generate the script on the server to prevent rerunning after hydration
    const scripts =
      typeof window === 'undefined'
        ? [
            {
              children: generateWindowConfigScript(),
              type: 'text/javascript',
            },
          ]
        : []

    const seoResult = seo({
      title: 'Spicy Friends',
      description:
        'Spicy Friends is a social network for swingers, poly, and lifestyle communities to connect, share naughty content, and explore open-minded relationships in a safe, inclusive space.',
      image: '/logo-og-image.webp',
    })

    const canonicalPathname = lastMatch.staticData?.canonicalPathname ?? lastMatch.pathname
    const canonicalHref = canonicalUrlForPathname(canonicalPathname)
    return {
      meta: [
        { charSet: 'UTF-8' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1.0' },
        { name: 'theme-color', content: '#1A202C' },
        ...seoResult.meta,
      ],
      links: [
        { rel: 'shortcut icon', href: '/favicon.ico' },
        { rel: 'apple-touch-icon', href: '/apple-touch-icon-180x180.png', sizes: '180x180' },
        { rel: 'mask-icon', href: '/SpicyFriends_Symbol_PWA.svg', color: '#1A202C' },
        { rel: 'stylesheet', href: appCss },
        { rel: 'stylesheet', href: tailwindCss },
        { rel: 'manifest', href: '/manifest.json' },
        ...(canonicalHref ? [{ rel: 'canonical', href: canonicalHref }] : []),
      ],
      scripts: [...(seoResult.scripts ?? []), ...scripts],
    }
  },

  shellComponent: RootDocument,

  beforeLoad: async ({ context }: { context: CustomRouterContext }) => {
    // Get the session data and current user data and provide in the context
    await context.queryClient.prefetchQuery({
      queryKey: authQueryKeys.session,
      queryFn: authUserSession,
      staleTime: 5 * 60 * 1000, // 5 minutes - matches server-side cookie cache
      gcTime: 10 * 60 * 1000, // 10 minutes
    })

    // Get the mapbox token and provide in the context
    await context.queryClient.prefetchQuery({
      queryKey: ['mapbox-token'],
      queryFn: () => getMapboxToken(),
      staleTime: 60 * 60 * 1000, // 1 hour
    })

    const session = context.queryClient.getQueryData(authQueryKeys.session) as BetterAuthSessionData | undefined
    const roles = session?.user?.roles

    return {
      userTheme: session?.user?.theme ?? null,
      seasonalThemesEnabled: session?.user?.seasonalThemesEnabled ?? true,
      isThemeAdmin: Boolean(roles && isValidRole(roles) && isAtLeastRole(roles, UserRole.ADMIN)),
      sideNavCookies: readSideNavSsrCookies(),
    }
  },
})

function RootDocument({ children }: { children: React.ReactNode }) {
  const { userTheme, seasonalThemesEnabled = true, isThemeAdmin = false, sideNavCookies = {} } = Route.useRouteContext()
  const { themeName, activeHoliday } = resolveActiveTheme({
    disableSeasonalTheme: useDisableSeasonalTheme(),
    seasonalThemesEnabled,
    selectedTheme: userTheme,
    isAdmin: isThemeAdmin,
  })
  const isLightTheme = isThemeName(themeName, 'light')

  // Register the service worker
  useEffect(() => {
    if (isServiceWorkerEnabled()) {
      ensureServiceWorkerRegistration().catch(console.error)
    } else {
      removeServiceWorkers().catch(console.error)
    }
  }, [])

  // Initialize third-party analytics after hydration (requestIdleCallback avoids SSR/hydration issues)
  useEffect(() => {
    const runWhenIdle = (fn: () => void) => {
      if (typeof window.requestIdleCallback !== 'undefined') {
        window.requestIdleCallback(fn, { timeout: 2000 })
      } else {
        setTimeout(fn, 0)
      }
    }

    runWhenIdle(() => {
      try {
        initGA()
        logPageView()
      } catch (error) {
        console.warn('Failed to initialize Google Analytics:', error)
      }
    })
  }, [])

  const content = (
    <ColorSchemeProvider activeHoliday={activeHoliday} themeName={themeName}>
      <SideNavCookieProvider cookies={sideNavCookies}>
        <I18nextProvider defaultNS={'translation'} i18n={i18n}>
          <TooltipProvider delay={200}>
            <PwaProvider>
              <PushNotificationProvider>
                <PullToRefreshProvider>
                  <SFProvider>
                    <SentryUserSync />
                    <Toaster />
                    {children}
                    <CookieConsentBanner />
                    <PwaInstallBar />
                    <TanStackDevtools
                      config={{
                        position: 'bottom-left',
                      }}
                      plugins={[
                        {
                          name: 'Tanstack Router',
                          render: <TanStackRouterDevtoolsPanel />,
                        },
                        {
                          name: 'Web Links',
                          render: <DevToolLinks />,
                        },
                        TanStackQueryDevtools,
                      ]}
                    />
                  </SFProvider>
                </PullToRefreshProvider>
              </PushNotificationProvider>
            </PwaProvider>
          </TooltipProvider>
        </I18nextProvider>
      </SideNavCookieProvider>
    </ColorSchemeProvider>
  )

  return (
    <html
      className={isLightTheme ? undefined : 'dark'}
      data-holiday={holidayAttributeForTheme(themeName)}
      lang="en"
      style={{ /* Turnstile Needs This colorScheme */ colorScheme: isLightTheme ? 'light' : 'dark' }}
    >
      <head>
        <HeadContent />
      </head>
      <body>
        {content}
        <Scripts />
      </body>
    </html>
  )
}
